import { FC, useEffect } from 'react';

import { useRecoilValue } from 'recoil';

import GiftItemPreview from '@service/giftStore/components/GiftItemPreview';
import {
  giftItemListForStoreDataAtomFamily,
  giftItemListForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import _ from 'lodash';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import GiftStoreFooter from '../components/GiftStoreFooter';
import { GiftDepositInfoBox } from '../containers/GiftDepositInfoBox/GiftDepositInfoBox';
import { CustomerCallCenterBox } from './CustomerCallCenterBox';

const GiftStoreMainPage: FC = function GiftStoreMainPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramsString = searchParams.toString();
  const bugoId = searchParams.get('bugoId');

  // bugoId가 없는 경우는 홈으로 보냄
  useEffect(() => {
    if (_.isNull(bugoId)) {
      navigate('/');
    }
  }, [bugoId, navigate]);

  useGiftItemListForStoreHook(bugoId ?? '');
  const status = useRecoilValue(giftItemListForStoreStatusAtomFamily(bugoId ?? ''));
  const data = useRecoilValue(giftItemListForStoreDataAtomFamily(bugoId ?? ''));

  const getImageUrl = (item: GiftItemForStore): string => {
    const image = item.image || item.giftItemInfo?.image;
    if (image && typeof image === 'object' && 'url' in image) {
      return fileBucketUrl((image as Image).url, 'w400');
    }
    return '';
  };

  const getName = (item: GiftItemForStore): string => {
    return item.name || item.giftItemInfo?.name || '';
  };

  const getType = (item: GiftItemForStore): GiftItemInfoType => {
    return item.giftItemInfo?.type || GiftItemInfoType.Delivery;
  };

  const getTags = (item: GiftItemForStore): string[] => {
    return item.giftItemInfo?.tags || [];
  };

  // 표시 순서 가져오기 (GiftItem.order 우선, 없으면 GiftItemInfo.order)
  const getDisplayOrder = (item: GiftItemForStore): number => {
    if (item.order !== undefined) return item.order;
    if (item.giftItemInfo?.order !== undefined) return item.giftItemInfo.order;
    return Number.MAX_SAFE_INTEGER;
  };

  // order 순서로 정렬된 데이터
  const sortedData = data
    ? [...data].sort((a, b) => getDisplayOrder(a) - getDisplayOrder(b))
    : null;

  return (
    <div>
      <CustomerCallCenterBox />
      {/* Item Preview List */}
      {status === 'success' && sortedData ? (
        sortedData.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-2 gap-y-2 p-2 py-4">
            {sortedData.map((item, idx) => (
              <Link
                key={`gift-item-link-${idx}`}
                to={`/gift-store/item/${item._id}?${paramsString}`}
                state={{
                  name: getName(item),
                }}
              >
                <GiftItemPreview
                  image={getImageUrl(item)}
                  name={getName(item)}
                  priceRegular={item.priceRegular}
                  priceRetail={item.priceRetail}
                  type={getType(item)}
                  tags={getTags(item)}
                />
              </Link>
            ))}
          </div>
        ) : (
          <div className="center-box h-screen-15 flex-col gap-2 text-gray-500">
            <p>등록된 답례품이 없습니다.</p>
          </div>
        )
      ) : (
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      )}
      <GiftDepositInfoBox />
      <GiftStoreFooter />
    </div>
  );
};

export default GiftStoreMainPage;
